import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import { PhoneField, isValidPhone, toE164 } from "/imports/ui/components/shared";

function GoogleProfileView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useWidget();
  const { googleCompleteProfile } = useAuth();

  const { profileToken, prefill = {}, redirect = "" } = location.state || {};

  const [phone, setPhone] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // The token from the Google step is the only thing authorizing account
  // creation. Reaching this page without one means there's nothing to finish.
  useEffect(() => {
    if (!profileToken) navigate(`/${slug}/`, { replace: true });
  }, [profileToken, navigate, slug]);

  if (!profileToken) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!phone.trim()) {
      setError("Please enter your phone number");
      return;
    }
    if (!isValidPhone(phone)) {
      setError("Please enter a valid phone number");
      return;
    }
    if (!acceptTerms) {
      setError("Please accept the terms and conditions to continue");
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      const data = await googleCompleteProfile(profileToken, toE164(phone));

      if (data?.preferencesToken) {
        navigate(`/${slug}/preferences`, { replace: true, state: { preferencesToken: data.preferencesToken, afterSave: "home", redirect } });
      } else {
        navigate(`/${slug}/${redirect}`, { replace: true });
      }
    } catch (err) {
      setError(err.message || "We couldn't finish setting up your account. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" py={4}>
      <Card sx={{ maxWidth: "31.25rem", width: "100%" }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 }, "&:last-child": { pb: { xs: 2.5, sm: 4 } } }}>
          <Typography variant="h5" fontWeight={600} textAlign="center" gutterBottom>
            Almost there
          </Typography>
          <Typography variant="body2" color="textSecondary" textAlign="center" mb={3}>
            We just need a phone number so your club can reach you about bookings.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="First Name" value={prefill.firstName || ""} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Last Name" value={prefill.lastName || ""} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Email" value={prefill.email || ""} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12}>
                <PhoneField value={phone} onChange={setPhone} required autoFocus />
              </Grid>
            </Grid>

            <FormControlLabel
              sx={{ mt: 2, alignItems: "flex-start" }}
              control={<Checkbox checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} sx={{ pt: 0 }} />}
              label={<Typography variant="body2">I agree to become a client of this facility and accept the terms and conditions</Typography>}
            />

            <Button type="submit" variant="contained" fullWidth size="large" disabled={submitting} sx={{ mt: 3 }}>
              {submitting ? "Creating account..." : "Continue"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}

export default GoogleProfileView;
